import { SearchResultsError } from './searchResultsError'
import { jsonRequest, JsonRequestError } from './jsonRequest'
import { search_books_path, search_friends_path } from '@/routes'
import type { BookSearchResult, UserSummary } from '@/types/generated'

/** The server owns matching, provider ordering, filtering and deduplication.
 * Progressive responses are complete snapshots, never provider-specific deltas.
 */
export async function searchBooks(
  query: string,
  signal: AbortSignal,
  onProgress?: (books: BookSearchResult[]) => void
): Promise<BookSearchResult[]> {
  // All book-search consumers start this immediately on input. Each new input
  // aborts both timers, so catalog pauses never trigger a Google Books request.
  signal.throwIfAborted()
  const controller = new AbortController()
  const abort = () => controller.abort()
  signal.addEventListener('abort', abort, { once: true })
  const activeSignal = controller.signal
  let results: BookSearchResult[] = []
  const publish = (books: BookSearchResult[]) => {
    if (activeSignal.aborted) return
    results = books
    onProgress?.(books)
  }

  // A catalog outage must not prevent the later combined lookup from trying
  // Google Books. The combined response owns the final error/result state.
  void waitForSearch(300, activeSignal)
    .then(() =>
      jsonRequest<BookSearchResult[]>(
        search_books_path({ q: query, catalog_only: true }),
        { signal: activeSignal }
      )
    )
    .then(publish)
    .catch(() => {})

  try {
    await waitForSearch(1000, activeSignal)
    return await requestBookResults(query, activeSignal, publish)
  } catch (error) {
    if (activeSignal.aborted || error instanceof SearchResultsError) throw error
    throw new SearchResultsError(
      'Book search failed. Please try again.',
      results
    )
  } finally {
    controller.abort()
    signal.removeEventListener('abort', abort)
  }
}

function waitForSearch(ms: number, signal: AbortSignal): Promise<void> {
  signal.throwIfAborted()
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer)
      reject(signal.reason)
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort)
      resolve()
    }, ms)
    signal.addEventListener('abort', abort, { once: true })
  })
}

async function requestBookResults(
  query: string,
  signal: AbortSignal,
  onProgress: (books: BookSearchResult[]) => void
): Promise<BookSearchResult[]> {
  const response = await fetch(search_books_path({ q: query, stream: true }), {
    signal,
    credentials: 'same-origin',
    mode: 'same-origin',
    headers: { Accept: 'application/x-ndjson' },
  })
  if (!response.ok)
    throw new JsonRequestError(
      'Book search failed. Please try again.',
      response.status
    )
  // JSON is also supported by the endpoint (including its empty-query reply).
  if (response.headers.get('Content-Type')?.includes('application/json')) {
    const books: unknown = await response.json()
    signal.throwIfAborted()
    if (!Array.isArray(books))
      throw new JsonRequestError('Invalid book search response.')
    return books as BookSearchResult[]
  }
  if (
    !response.headers.get('Content-Type')?.includes('application/x-ndjson') ||
    !response.body
  ) {
    throw new JsonRequestError('Invalid book search response.')
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let pending = ''
  try {
    while (true) {
      const { value, done } = await reader.read()
      signal.throwIfAborted()
      pending += decoder.decode(value, { stream: !done })
      let newline: number
      while ((newline = pending.indexOf('\n')) >= 0) {
        const line = pending.slice(0, newline)
        pending = pending.slice(newline + 1)
        if (!line.trim()) continue
        const snapshot = JSON.parse(line)
        if (
          !Array.isArray(snapshot.results) ||
          typeof snapshot.complete !== 'boolean' ||
          (snapshot.error != null && typeof snapshot.error !== 'string')
        ) {
          throw new JsonRequestError('Invalid book search response.')
        }
        if (snapshot.complete) {
          if (snapshot.error) {
            throw new SearchResultsError(
              snapshot.error,
              snapshot.results as BookSearchResult[]
            )
          }
          return snapshot.results as BookSearchResult[]
        }
        onProgress?.(snapshot.results as BookSearchResult[])
      }
      if (done)
        throw new JsonRequestError(
          'Book search was interrupted. Please try again.'
        )
    }
  } finally {
    await reader.cancel().catch(() => {})
    reader.releaseLock()
  }
}

export async function searchFriends(
  query: string,
  signal: AbortSignal
): Promise<UserSummary[]> {
  return jsonRequest<UserSummary[]>(search_friends_path({ q: query }), {
    signal,
  })
}
